/**
 * 右上角用户菜单: 头像 (+ 名字) → 下拉: 名字 / 公司 · 角色, 自定义入口, 账号设置, 退出。
 * Esc / 点外面关闭; 方向键在菜单项间移动。
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut, UserCog, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ROLE_LABEL, useShell } from "./useShell";

export interface UserMenuLink {
  to: string;
  label: string;
  icon: LucideIcon;
  /** SPA 之外的地址 (如旧版 /static/): 用普通 <a> 整页跳转 */
  external?: boolean;
}

export function UserMenu({
  links = [],
  showName = true,
  className,
  inverse = false,
}: {
  /** 绿色顶栏上使用 (白字) */
  inverse?: boolean;
  /** 账号设置之前的额外入口 (如手机上的「管理后台」) */
  links?: UserMenuLink[];
  /** md 以上在头像旁显示名字 */
  showName?: boolean;
  className?: string;
}) {
  const { user, logout } = useShell();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (!user) return null;
  const name = user.display_name || user.username;
  const initial = name.trim().slice(0, 1).toUpperCase();
  const sub = [user.company_name, ROLE_LABEL[user.role] ?? user.role].filter(Boolean).join(" · ");

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const itemCls =
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground hover:bg-muted focus:bg-muted focus:outline-none [&_svg]:size-4 [&_svg]:text-muted-foreground";

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`用户菜单: ${name}`}
        className={cn("flex h-9 items-center gap-2 rounded-full pl-0.5 pr-1 transition-colors focus-visible:outline-none focus-visible:ring-2 md:pr-2", inverse ? "text-white hover:bg-white/15 focus-visible:ring-white/70" : "hover:bg-muted focus-visible:ring-ring")}
      >
        <span
          aria-hidden
          className={cn("flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold", inverse ? "bg-white text-primary" : "bg-primary-soft text-primary")}
        >
          {initial}
        </span>
        {showName && <span className="hidden max-w-[8rem] truncate text-sm font-medium md:inline">{name}</span>}
        <ChevronDown aria-hidden className={cn("hidden h-4 w-4 md:block", inverse ? "text-white/80" : "text-muted-foreground")} />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="用户菜单"
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 duration-100"
        >
          <div className="px-2.5 py-2">
            <div className="truncate text-sm font-semibold">{name}</div>
            <div className="truncate text-xs text-muted-foreground">{sub}</div>
          </div>
          <div className="my-1 h-px bg-border" />
          {links.map((l) =>
            l.external ? (
              <a key={l.to} href={l.to} role="menuitem" className={itemCls}>
                <l.icon aria-hidden />
                {l.label}
              </a>
            ) : (
              <Link key={l.to} to={l.to} role="menuitem" className={itemCls} onClick={() => close(false)}>
                <l.icon aria-hidden />
                {l.label}
              </Link>
            ),
          )}
          <Link to="/account" role="menuitem" className={itemCls} onClick={() => close(false)}>
            <UserCog aria-hidden />
            账号设置
          </Link>
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            className={cn(itemCls, "text-danger-strong [&_svg]:text-danger-strong")}
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <LogOut aria-hidden />
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}
